import './PasswordField.scss';
import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, InputProps } from 'src/modules/common/components/Input';
import { Tooltip } from 'src/modules/common/components/Tooltip';
import { VisibilityIcon } from 'src/modules/common/icons/VisibilityIcon';
import { VisibilityOffIcon } from 'src/modules/common/icons/VisibilityOffIcon';
import { FormError } from 'src/modules/form/types/FormError';

type Props<TData> = Omit<
InputProps,
'type'
| 'name'
| 'id'
| 'value'
| 'invalid'
| 'invalidText'
| 'onChange'
| 'onBlur'
| 'actions'
> & {
  readonly field: FieldPath<TData, FormError, string>;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const PasswordField: <TData>(props: Props<TData>) => React.ReactElement = (
  {
    field,
    renderError = renderErrorDefault,
    ...props
  },
) => {
  const intl = useIntl();
  const { meta, value, error } = useFormField(field);

  const [showPassword, setShowPassword] = useState(false);

  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    field.form.change(pipe(
      field.form.currentState,
      field.value.set(event.target.value),
    ));
  }, [field]);

  const handleBlur = useCallback(() => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));
  }, [field]);

  return (
    <Input
      {...props}
      type={showPassword ? 'text' : 'password'}
      name={field.path}
      id={`${field.form.name}.${field.path}`}
      value={value}
      invalid={invalid}
      invalidText={error && invalid ? renderError(error) : null}
      onChange={handleChange}
      onBlur={handleBlur}
      actions={(
        <Tooltip
          id={`bp-password-field-${field.path}-toggle-visibility`}
          value={showPassword
            ? intl.formatMessage({ id: 'form/field/password/hide' })
            : intl.formatMessage({ id: 'form/field/password/show' })}
        >
          <button
            type="button"
            className="bp-password-field__button"
            onClick={() => setShowPassword((prev) => !prev)}
          >
            <div className="bp-password-field__icon">
              {showPassword ? <VisibilityOffIcon/> : <VisibilityIcon/>}
            </div>
          </button>
        </Tooltip>
      )}
    />
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
);
